import { GetStaticProps } from 'next';
import { RegioExpresHomePageFieldsFragment, RegioExpresInformationFieldsFragment } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetRegioExpresHome } from 'api/graphql-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { Head } from 'modules/head/head';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import HomepageHeader from 'modules/homepage-header/homepage-header';
import { REGIO_EXPRESS_ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface IRegioExpresPageProps extends IBasePageProps {
	regioExpresHomepage?: RegioExpresHomePageFieldsFragment;
	regioExpresInformation?: RegioExpresInformationFieldsFragment;
}

const RegioExpresHomePage = (props: IRegioExpresPageProps): React.JSX.Element => {
	const modules = props.regioExpresHomepage?.modules;
	const seoModule = props?.regioExpresHomepage?.seo;

	return (
		<Layout navbar={props?.regioExpresInformation?.navbar} footer={props?.regioExpresInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.regioExpresInformation?.seo} />
			<HomepageHeader module={props?.regioExpresHomepage?.header} />
			<ModuleBuilder modules={modules} slug={REGIO_EXPRESS_ROUTES.HOME.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const regioExpresHomePageResult = await getServerPageGetRegioExpresHome(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { regioExpresHomepage, regioExpresInformation } = regioExpresHomePageResult.props.data;

		if (regioExpresHomepage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				regioExpresHomepage: regioExpresHomepage.data?.attributes,
				regioExpresInformation: regioExpresInformation?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'regio-expres-homepage',
						contentTypeId: regioExpresHomepage?.data?.documentId,
						showNeutralStyle: true,
						hideLanguageSwitcher: true,
						hideSearchbar: false,
						isSubsite: true
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('error in RegioExpresHomePage', error);
		return {
			notFound: true
		};
	}
};

export default RegioExpresHomePage;
